import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  CircularProgress,
  Link,
  Theme,
  Typography,
  styled,
  useMediaQuery,
} from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { FormErrorMessage } from "@components";
import {
  BottomActionArea,
  BottomActionButton,
  CodeInput,
  FullScreenLoader,
  SnackBar,
  NavigateBeforeButton,
  ExtraWhiteSpace,
  FormHeader,
  FormContentContainer,
  FormBody,
} from "@components";
import { RequestType } from "@interfaces";
import { apiGateway, queryClient } from "@contexts";
import {
  getFromLocalStorage,
  getUnitForQuantity,
  onContactUsClick,
  redirectToPinScreenResolver,
} from "@utils";
import { needHelpMessage } from "../../constants";
import { millisecondsInSecond } from "date-fns";
import { AxiosError } from "axios";
import { ErrorType } from "./hooks/onQuestionChallengeError";
import { useEnrolmentStatusContext } from "@contexts/EnrolmentStatusContext";

type CreateCertificateStatus =
  | "NOT_STARTED"
  | "CREATING_CERTIFICATE_APPLICATION"
  | "DOWNLOADING_CERTIFICATE"
  | "SUCCESS"
  | "ERROR";

const PinContainer = styled(Box)({
  display: "flex",
  flex: 1,
  flexDirection: "column",
  marginTop: "64px",
});

const Title = styled(Typography)(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    display: "none",
  },
}));

const PinLabel = styled(Typography)({
  textAlign: "center",
  padding: "16px",
});

const PinErrorContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
});

const ContactUsContainer = styled(Box)({
  paddingTop: "20px",
});

const Bold = styled("span")({
  fontWeight: 600,
});

const PinDisclaimerContainer = styled(Box)({
  display: "flex",
  padding: "32px 0px 16px 0px",
});

const PinDisclaimer = styled(Typography)(({ theme }) => ({
  fontSize: "14px",
  textAlign: "center",
  color: theme.palette.text.secondary,
}));

const TitleScreenLoader = styled(Typography)({
  fontSize: "1.25rem",
  fontWeight: 500,
  textAlign: "center",
});

const SubtitleLoader = styled(Typography)({
  textAlign: "center",
});

export function CreatePinScreen() {
  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up("md"));
  const navigate = useNavigate();
  const { setCurrentStep, setCurrentStepStatus, getEnrolmentStatus } =
    useEnrolmentStatusContext();
  const [searchParams] = useSearchParams();
  const requestId = searchParams.get("requestId");
  const [pin, setPin] = useState("");
  const [isPollingStatus, setIsPollingStatus] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(30);
  const [isCreatingMagicLink, setIsCreatingMagicLink] = useState(false);

  const requestType = getFromLocalStorage<RequestType>({
    key: "requestType",
  }) as RequestType;

  const { mutate: createCertificate, isPending: creatingCertIsPending } =
    useMutation({
      mutationFn: (pin: string) => {
        setIsPollingStatus(true);
        return apiGateway({
          method: "post",
          url: "/enrolment/certificate",
          data: { pin },
        });
      },
      onError: async (error: AxiosError<ErrorType>) => {
        const errorData = error.response?.data;
        const nextStep = errorData?.nextStep;
        const nextStepStatus = errorData?.nextStepStatus;
        if (nextStep && nextStepStatus) {
          setCurrentStep(nextStep);
          setCurrentStepStatus(nextStepStatus);
        } else {
          await getEnrolmentStatus();
        }
      },
    });

  const { data: certificateCreationStatus } = useQuery({
    queryKey: ["certificateCreationStatus"],
    queryFn: () =>
      apiGateway<{ status: CreateCertificateStatus }>({
        method: "get",
        url: "/enrolment/certificate/status",
      }),
    enabled: isPollingStatus,
    refetchInterval: 3 * millisecondsInSecond,
    initialData: { status: "NOT_STARTED" },
  });

  const { isCertificateReadyToUse, isCertificateFailed } = useMemo(() => {
    if (!certificateCreationStatus)
      return { isCertificateReadyToUse: false, isCertificateFailed: false };
    const { status } = certificateCreationStatus;
    return {
      isCertificateReadyToUse: status === "SUCCESS",
      isCertificateFailed: status === "ERROR",
    };
  }, [certificateCreationStatus]);

  useEffect(() => {
    if (!isCertificateReadyToUse) return;
    queryClient.invalidateQueries({ queryKey: ["doctor"] });
    navigate(redirectToPinScreenResolver(requestId, requestType));
  }, [isCertificateReadyToUse, requestId, requestType, navigate]);

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleNextScreen = useCallback(async () => {
    if (pin.length === 4) {
      setIsCreatingMagicLink(true);
      await apiGateway({ method: "POST", url: "/auth/create-magic-link" });
      setIsCreatingMagicLink(false);
      createCertificate(pin);
    }
  }, [pin, createCertificate]);

  const handlePinChange = (val: string) => {
    setPin(val);
  };

  const isWaitingForCertificateCreation =
    isCreatingMagicLink ||
    creatingCertIsPending ||
    (isPollingStatus &&
      !["SUCCESS", "ERROR"].includes(certificateCreationStatus.status));

  useEffect(() => {
    function updateRemainingSeconds() {
      let timerId: NodeJS.Timer | null = null;
      if (isWaitingForCertificateCreation && remainingSeconds > 0) {
        timerId = setInterval(() => {
          setRemainingSeconds((prev) => Math.max(prev - 1, 0));
        }, 1000);
      }
      return () => {
        if (timerId) clearInterval(timerId);
      };
    }
    return updateRemainingSeconds();
  }, [isWaitingForCertificateCreation, remainingSeconds]);

  if (isWaitingForCertificateCreation)
    return (
      <>
        <FormHeader
          title={!isDesktop ? "Verificación de identidad" : undefined}
          useMekiDocIcon={isDesktop}
          titleColor="white"
          left={<NavigateBeforeButton iconColor="white" onClick={handleBack} />}
        />
        <FormContentContainer disableGutters>
          <FormBody justifyContent="center">
            <FullScreenLoader>
              <TitleScreenLoader>
                {remainingSeconds
                  ? "Estamos registrando tu firma electrónica"
                  : "Estamos demorando un poco más de lo esperado."}
              </TitleScreenLoader>
              <SubtitleLoader>
                {remainingSeconds
                  ? `Este proceso puede durar ${remainingSeconds} ${getUnitForQuantity(
                      "segundo",
                      remainingSeconds,
                    )}`
                  : "Danos unos segundos más para finalizar el registro."}
              </SubtitleLoader>
            </FullScreenLoader>
          </FormBody>
        </FormContentContainer>
      </>
    );

  return (
    <>
      <FormHeader
        title={!isDesktop ? "Verificación de identidad" : undefined}
        useMekiDocIcon={isDesktop}
        titleColor="white"
        left={<NavigateBeforeButton iconColor="white" onClick={handleBack} />}
      />
      <FormContentContainer disableGutters>
        <FormBody>
          <Title variant="h5" fontWeight={"medium"}>
            Verificación de identidad
          </Title>
          <PinContainer>
            <PinLabel>
              Crea un <Bold>PIN de 4 dígitos</Bold>. Te lo pediremos cada vez
              que quieras firmar una receta
            </PinLabel>
            <CodeInput
              code={pin}
              handleCodeChange={handlePinChange}
              numInputs={4}
              isValid={!isCertificateFailed}
              type={"number"}
            />
            {isCertificateFailed && (
              <PinErrorContainer>
                <FormErrorMessage>
                  Ha ocurrido un error con la creación de tu PIN
                </FormErrorMessage>
                <ContactUsContainer>
                  Si el problema persiste{" "}
                  <Link
                    onClick={() => {
                      onContactUsClick(needHelpMessage);
                    }}
                  >
                    contáctanos
                  </Link>
                </ContactUsContainer>
              </PinErrorContainer>
            )}
            <PinDisclaimerContainer>
              <PinDisclaimer>
                <Bold>Elige un PIN que te sea fácil de recordar.</Bold> En caso
                de olvidarlo tendrás que volver a verificar tu identidad
              </PinDisclaimer>
            </PinDisclaimerContainer>
          </PinContainer>

          <ExtraWhiteSpace />

          <SnackBar
            snackBarState={{
              open: isCertificateReadyToUse,
              type: "success",
            }}
            alerts={{
              success: "Registro exitoso",
            }}
          />
          <BottomActionArea>
            <BottomActionButton
              onClick={handleNextScreen}
              disabled={pin.length !== 4 || isWaitingForCertificateCreation}
            >
              {isWaitingForCertificateCreation ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Siguiente"
              )}
            </BottomActionButton>
          </BottomActionArea>
        </FormBody>
      </FormContentContainer>
    </>
  );
}
